/**
 * SearchBar Component
 * Reusable search bar with optional submit button and clear functionality
 */

import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import './SearchBar.css';

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  showSubmitButton?: boolean;
  submitButtonText?: string;
  className?: string;
  autoFocus?: boolean;
}

export interface SearchBarRef {
  focus: () => void;
}

const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = '搜尋...',
  showSubmitButton = false,
  submitButtonText = '搜尋',
  className = '',
  autoFocus = false,
}, ref) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.(value.trim());
  };

  const handleClear = () => {
    onChange('');
    onClear?.();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClear();
    }
  };

  const content = (
    <div className={`search-bar ${className}`}>
      <div className="search-bar__wrapper">
        <svg
          className="search-bar__icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          ref={inputRef}
          type="search"
          className="search-bar__input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label={placeholder}
          autoFocus={autoFocus}
        />
        {value && (
          <button
            type="button"
            className="search-bar__clear"
            onClick={handleClear}
            aria-label={t('clearSearch')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>
      {showSubmitButton && (
        <button type="submit" className="search-bar__submit">
          {submitButtonText}
        </button>
      )}
    </div>
  );

  if (onSubmit) {
    return (
      <form className="search-bar__form" onSubmit={handleSubmit}>
        {content}
      </form>
    );
  }

  return content;
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
